/**
 * UranIA PA — Env vars accessor (US-009).
 *
 * Tutte le env Vite sono `VITE_*` (resto = stripped a build time).
 * Niente segreti (lato client niente client_secret, ADR-005 §3 + ADR-006 §6):
 * il flusso OIDC è code+PKCE pubblico.
 *
 * Fail-fast (2026-09-08): in build di produzione (`DEV=false`) una VITE_*
 * richiesta mancante o vuota non viene più sostituita in silenzio da un
 * default `localhost` — farebbe puntare OIDC/API dal browser di un utente
 * reale verso la sua stessa macchina, non verso un errore visibile.
 * `loadSpaEnv()` lancia qui, e `main.tsx` la chiama prima del primo render
 * così un deploy mal configurato si ferma subito invece di raggiungere
 * l'utente. I default `localhost` restano disponibili SOLO con `DEV=true`
 * (server di sviluppo `vite`), mai in un bundle di produzione.
 */

export interface SpaEnv {
  readonly oidcAuthority: string;
  readonly oidcClientId: string;
  readonly oidcRedirectUri: string;
  readonly oidcPostLogoutRedirectUri: string;
  readonly oidcSilentRedirectUri: string;
  readonly apiBaseUrl: string;
  readonly auditUiEndpoint: string;
}

/** Sottoinsieme di `ImportMetaEnv` con cui `buildSpaEnv` lavora — iniettabile nei test senza dover mockare `import.meta.env`. */
export interface RawSpaEnv {
  readonly DEV: boolean;
  readonly VITE_OIDC_AUTHORITY?: string;
  readonly VITE_OIDC_CLIENT_ID?: string;
  readonly VITE_OIDC_REDIRECT_URI?: string;
  readonly VITE_OIDC_POST_LOGOUT_REDIRECT_URI?: string;
  readonly VITE_OIDC_SILENT_REDIRECT_URI?: string;
  readonly VITE_API_BASE_URL?: string;
  readonly VITE_AUDIT_UI_ENDPOINT?: string;
}

function readRequired(raw: RawSpaEnv, name: keyof RawSpaEnv, devFallback: () => string): string {
  const value = raw[name];
  if (typeof value === 'string' && value.length > 0) {
    return value;
  }
  if (raw.DEV) {
    return devFallback();
  }
  throw new Error(
    `UranIA PA: configurazione di build incompleta — variabile richiesta mancante o vuota: ${name}. ` +
      "Controlla gli ARG passati al build della SPA (Dockerfile.spa / docker-compose args, guidati da URANIA_DOMAIN).",
  );
}

function readOptional(raw: RawSpaEnv, name: keyof RawSpaEnv, fallback: string): string {
  const value = raw[name];
  return typeof value === 'string' && value.length > 0 ? value : fallback;
}

/**
 * Costruisce `SpaEnv` da un `RawSpaEnv` iniettato. Esportata (oltre a
 * `loadSpaEnv`) solo per essere testata senza mockare `import.meta.env` —
 * non chiamarla altrove nell'app, usa `loadSpaEnv()`.
 */
export function buildSpaEnv(raw: RawSpaEnv, origin: string): SpaEnv {
  return {
    oidcAuthority: readRequired(
      raw,
      'VITE_OIDC_AUTHORITY',
      () => 'http://localhost:8080/realms/urania-pa-staging',
    ),
    oidcClientId: readRequired(raw, 'VITE_OIDC_CLIENT_ID', () => 'spa-staging'),
    oidcRedirectUri: readRequired(raw, 'VITE_OIDC_REDIRECT_URI', () => `${origin}/auth/callback`),
    oidcPostLogoutRedirectUri: readRequired(raw, 'VITE_OIDC_POST_LOGOUT_REDIRECT_URI', () => origin),
    oidcSilentRedirectUri: readRequired(
      raw,
      'VITE_OIDC_SILENT_REDIRECT_URI',
      () => `${origin}/auth/silent-renew`,
    ),
    apiBaseUrl: readRequired(raw, 'VITE_API_BASE_URL', () => 'http://localhost:8000'),
    // Feature opzionale (endpoint audit UI disattivato se assente): mai richiesta.
    auditUiEndpoint: readOptional(raw, 'VITE_AUDIT_UI_ENDPOINT', ''),
  };
}

export function loadSpaEnv(): SpaEnv {
  return buildSpaEnv(import.meta.env, window.location.origin);
}
